import { SubscriptionsWarningState } from './enums';
import { URLS } from './urls';
import {
  ACTIONS,
  AnalyticsEventPropTypes,
  AnalyticsEventWithControlName,
  SubscriptionType
} from '@uma/react-commons/dist/index';

const activity = 'UnifiedAcctMgmt-v01';
export const screenPath = '/ReactMyAccountSubscriptions/';
export const screenName = 'SubscriptionsAndServices';
const eventDetailVersion = '1.5.0';
export const screenPathSubscriptionDetails =
  '/ReactMyAccountSubscriptions/SubscriptionsAndServices/';
const screenPathSubscriptionDetailsError =
  '/ReactMyAccountSubscriptions/SubscriptionsAndServices/SubscriptionDetails/';

export const screenNameSubscriptionDetails = 'SubscriptionDetails';
const screenNameSubscriptionsError = 'SubscriptionsError';
const screenNameSubscriptionDetailsError = 'SubscriptionDetailsError';

function getSubTypeNameFormatted(subscriptionType: SubscriptionType): string {
  switch (subscriptionType) {
    case SubscriptionType.INSTANT_INK:
      return 'InstantInk';
    case SubscriptionType.HP_ALL_IN:
      return 'HpAllIn';
    default:
      return 'Unknown';
  }
}

export function parseType(type: keyof typeof SubscriptionType): string {
  let controlName: string = '';
  switch (type) {
    case SubscriptionType.INSTANT_INK:
      controlName = 'HpInstantInk';
      break;
    case SubscriptionType.HP_ALL_IN:
      controlName = 'HpAllIn';
      break;
    case SubscriptionType.HP_CARE_PACK:
      controlName = 'HpCarePack';
      break;
    default:
      break;
  }
  return controlName;
}

export function getActionAuxParamsSubscriptionsDetails(
  id: string,
  startDate: string | Date
): string {
  return `subscriptionStartDate=${startDate ?? ''}&subscriptionId=${id}`;
}

export const ModuleDisplayedErrorSubscription: AnalyticsEventPropTypes = {
  action: ACTIONS.MODULE_DISPLAYED,
  activity,
  screenPath: screenPathSubscriptionDetails,
  screenName: screenNameSubscriptionsError,
  version: eventDetailVersion
};

export const ModuleDisplayedErrorSubscriptionDetails: AnalyticsEventPropTypes =
  {
    action: ACTIONS.MODULE_DISPLAYED,
    activity,
    screenPath: screenPathSubscriptionDetailsError,
    screenName: screenNameSubscriptionDetailsError,
    version: eventDetailVersion
  };

export function getModuleDisplayedError(
  errorMessage: string
): AnalyticsEventPropTypes {
  return {
    ...ModuleDisplayedErrorSubscription,
    actionAuxParams: `error=${errorMessage?.slice(0, 256)}`
  };
}

export function getModuleDisplayedSubscriptionsDetailsError(
  errorMessage: string
): AnalyticsEventPropTypes {
  return {
    ...ModuleDisplayedErrorSubscriptionDetails,
    actionAuxParams: `error=${errorMessage?.slice(0, 256)}`
  };
}

export const SubscriptionDetailsProps = {
  activity,
  screenPath: screenPathSubscriptionDetails,
  screenName: screenNameSubscriptionDetails,
  version: eventDetailVersion
};

export function generateAnalyticsOfScreenDisplayedByType(
  type: keyof typeof SubscriptionType
): AnalyticsEventPropTypes {
  const screenMode = parseType(type);

  return {
    ...SubscriptionDetailsProps,
    action: ACTIONS.SCREEN_DISPLAYED,
    screenMode
  } as AnalyticsEventPropTypes;
}

export const ScreenDisplayed = (
  subscriptionsLength: number,
  hpInstantInkLength: number,
  hpAllInLength: number
) =>
  ({
    action: ACTIONS.SCREEN_DISPLAYED,
    activity,
    screenPath,
    screenName,
    version: eventDetailVersion,
    actionAuxParams: `totalSubscriptionsCt=${subscriptionsLength}&totalInstantInkCt=${hpInstantInkLength}&totalHpAllInCt=${hpAllInLength}`
  }) as AnalyticsEventPropTypes;

export const ChangeYourInstantInkPlanButtonClicked: AnalyticsEventWithControlName =
  {
    ...SubscriptionDetailsProps,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
    controlName: 'ChangeYourInstantInkPlanButton',
    controlDetail: URLS.ChangeYourPlanPrint
  };

export const ChangeYourAllInPlanButtonClicked: AnalyticsEventWithControlName = {
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_BUTTON_CLICKED,
  controlName: 'ChangeYourAllInPlanButton',
  controlDetail: URLS.ChangeYourPlanAllIn
};

export const CancelHPInstantInkButtonClicked: AnalyticsEventWithControlName = {
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_BUTTON_CLICKED,
  controlName: 'CancelHPInstantInkButton',
  controlDetail: URLS.CancelHPInstantInk
};

export const CancelHPAllInButtonClicked = (
  controlDetail: string
): AnalyticsEventWithControlName => {
  return {
    ...SubscriptionDetailsProps,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
    controlName: 'CancelYourHpAllInButton',
    controlDetail
  };
};

export const HPStoreSubscriptionHyperLinkClicked = (
  controlDetail: string
): AnalyticsEventWithControlName => {
  return {
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
    activity,
    screenPath,
    screenName,
    version: eventDetailVersion,
    controlName: 'DontSeeSubscriptionsLink',
    controlDetail
  };
};

export function generateAnalyticsOfSubscriptionCardClickedByType(
  type: keyof typeof SubscriptionType,
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName {
  const controlName = parseType(type);

  return {
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
    activity,
    screenPath,
    screenName,
    version: eventDetailVersion,
    controlName: `${controlName}SubscriptionCardClicked`,
    actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
  };
}

export const SubscriptionWarningModuleDisplayed = (
  subscriptionType: SubscriptionType,
  warningType: SubscriptionsWarningState
): AnalyticsEventWithControlName => ({
  action: ACTIONS.MODULE_DISPLAYED,
  activity,
  screenPath: screenPathSubscriptionDetails,
  screenName: screenNameSubscriptionDetails,
  screenMode: getSubTypeNameFormatted(subscriptionType),
  version: eventDetailVersion,
  controlName: 'SubscriptionStatusBannerModule',
  actionAuxParams: `warningType=${warningType?.toLowerCase()}`
});

export const SubscriptionWarningButtonClicked = (
  subscriptionType: SubscriptionType,
  warningType: SubscriptionsWarningState
): AnalyticsEventWithControlName => ({
  action: ACTIONS.CONTROL_BUTTON_CLICKED,
  activity,
  screenPath: screenPathSubscriptionDetails,
  screenName: screenNameSubscriptionDetails,
  screenMode: getSubTypeNameFormatted(subscriptionType),
  version: eventDetailVersion,
  controlName: 'SubscriptionStatusBannerButton',
  controlDetail:
    subscriptionType === SubscriptionType.HP_ALL_IN
      ? URLS.HpAllInOverview
      : URLS.InstantInkOverview,
  actionAuxParams: `warningType=${warningType?.toLowerCase()}`
});

export const InstantInkManagePaymentMethodHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  screenMode: 'HpInstantInk',
  controlName: 'ManageInstantInkPaymentMethodLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const HpAllInManagePaymentMethodHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  screenMode: 'HpAllIn',
  controlName: 'ManageAllInPaymentMethodLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const InstantInkAddPaymentMethodHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  screenMode: 'HpInstantInk',
  controlName: 'AddInstantInkPaymentMethodLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const HpAllInAddPaymentMethodHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  screenMode: 'HpAllIn',
  controlName: 'AddAllInPaymentMethodLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const PaymentHistoryAllInHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'PaymentHistoryAllInLink',
  controlDetail: URLS.AccountHistoryAllIn,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const PaymentHistoryInstantInkHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'PaymentHistoryInstantInkLink',
  controlDetail: URLS.AccountHistoryInk,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const ShipmentHistoryAllInHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'ShipmentHistoryAllInLink',
  controlDetail: URLS.AllInShipment,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const ShipmentHistoryInstantInkHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'ShipmentHistoryInstantInkLink',
  controlDetail: URLS.InkShipment,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const OverviewInstantInkHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'InstantInkOverviewLink',
  controlDetail: URLS.InstantInkOverview,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const OverviewAllInHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'HpAllInOverviewLink',
  controlDetail: URLS.HpAllInOverview,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const HpAllInManageAddressHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'ChangeAddressAllInLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});

export const InstantInkManageAddressHyperLinkClicked = (
  id: string,
  startDate: string | Date
): AnalyticsEventWithControlName => ({
  ...SubscriptionDetailsProps,
  action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
  controlName: 'ChangeAddressInstantInkLink',
  controlDetail: URLS.UnifiedShippingBillingURL,
  actionAuxParams: getActionAuxParamsSubscriptionsDetails(id, startDate)
});
